import React from 'react';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from 'recharts';

// Tight inline trend line for the monitor card. Mirrors the old
// MetricSparkline but reads monitor_values shape (.value not
// .metric_value) and falls back gracefully when there's <2 points.
const MonitorSparkline = ({ values = [] }) => {
  const points = Array.isArray(values) ? values : [];

  if (points.length < 2) {
    return (
      <div className="h-12 flex items-center text-xs text-gray-400">
        {points.length === 0
          ? 'No history yet — run the monitor to see a trend.'
          : 'Run again to see a trend.'}
      </div>
    );
  }

  // Backend returns DESC by calculated_at. Flip so the line reads
  // left-to-right as oldest-to-newest.
  const data = [...points]
    .reverse()
    .map((row) => ({
      t: row.calculated_at,
      v: typeof row.value === 'number' ? row.value : Number(row.value),
    }))
    .filter((p) => Number.isFinite(p.v));

  if (data.length < 2) {
    return (
      <div className="h-12 flex items-center text-xs text-gray-400">
        Run again to see a trend.
      </div>
    );
  }

  const TinyTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
      <div className="bg-white text-xs px-2 py-1 rounded shadow border border-gray-200">
        <div className="font-medium text-gray-900">
          {Number(point.v).toLocaleString()}
        </div>
        <div className="text-gray-500">{new Date(point.t).toLocaleString()}</div>
      </div>
    );
  };

  // Y-axis domain. We can't use the literal ['dataMin', 'dataMax']:
  // recharts auto-zooms tightly to that range, and combined with the
  // monotone curve interpolation on the line, even 0.5%-of-magnitude
  // variations get rendered as dramatic sine waves. A monitor whose
  // value drifts ±0.3% looks identical to one that doubled.
  //
  // Set a MINIMUM display range relative to the mean magnitude so
  // small variations look small. With minDisplayRange = 10% of |mean|:
  //   • 0% variation       → centered flat line
  //   • 0.5% variation     → barely-visible wiggle (~5% of chart height)
  //   • 10% variation      → fills the chart (normal trend)
  //   • >10% variation     → no floor applied, recharts auto-scales
  //
  // The exact-equality (max === min) case is now subsumed: actualRange = 0
  // triggers full padding from the floor.
  //
  // (User report 2026-05-21: custom_segment monitor with min=452.24B,
  // max=453.82B (~0.35% variation) rendered as a clean sine wave.)
  const vals = data.map((p) => p.v);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const meanAbs = Math.abs(vals.reduce((a, b) => a + b, 0) / vals.length);
  const actualRange = maxV - minV;
  // Floor in absolute terms — Math.max guards against meanAbs=0
  // (a series of zeroes) where 10% of 0 would also be 0.
  const minDisplayRange = Math.max(meanAbs * 0.1, 1);
  const padding =
    actualRange < minDisplayRange
      ? (minDisplayRange - actualRange) / 2
      : 0;
  const yDomain = [minV - padding, maxV + padding];

  return (
    <div className="h-12 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <YAxis hide domain={yDomain} />
          <Tooltip content={<TinyTooltip />} cursor={{ stroke: '#a5b4fc' }} />
          <Line
            type="monotone"
            dataKey="v"
            stroke="#6366f1"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MonitorSparkline;
